import React, { useState } from 'react';
import SquarePayment from './SquarePayment';
import { registerPassenger, createBooking } from '../api/squareApi';

const BookingFlow = ({ 
  initialBookingData = {}, 
  onBookingComplete,
  onError 
}) => {
  const [step, setStep] = useState('booking'); // 'booking', 'payment', 'complete'
  const [bookingData, setBookingData] = useState(initialBookingData);
  const [bookingRefId, setBookingRefId] = useState(null);
  const [passengerId, setPassengerId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    // Passenger details
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    
    // Booking details
    pickup_location: '',
    dropoff_location: '',
    pickup_date: '',
    pickup_time: '',
    fleet_id: '',
    journey_fare: '',
    final_fare: '',
    passenger_count: 1,
    
    // Additional details
    special_instructions: '',
    flight_number: '',
    payment_type: 'FULL' // 'FULL' or 'PARTIAL'
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear specific error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Required fields validation
    const requiredFields = [
      'first_name', 'last_name', 'email', 'phone',
      'pickup_location', 'dropoff_location', 'pickup_date', 'pickup_time',
      'final_fare'
    ];
    
    requiredFields.forEach(field => {
      if (!formData[field] || formData[field].toString().trim() === '') {
        newErrors[field] = `${field.replace('_', ' ')} is required`;
      }
    });

    // Email validation
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone validation
    if (formData.phone && !/^\+?[\d\s\-()]{10,}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    // Fare validation
    if (formData.final_fare && (isNaN(formData.final_fare) || parseFloat(formData.final_fare) <= 0)) {
      newErrors.final_fare = 'Please enter a valid fare amount';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      // 1. Register passenger
      const passengerData = {
        customer_title: 'Mr', // Default title, could be made configurable
        customer_first_name: formData.first_name,
        customer_last_name: formData.last_name,
        customer_email: formData.email,
        customer_cell_no: formData.phone
      };

      const passengerResponse = await registerPassenger(passengerData);
      const newPassengerId = passengerResponse.passenger_id || passengerResponse.id;
      setPassengerId(newPassengerId);

      // 2. Create booking
      const journeyDateTime = `${formData.pickup_date} ${formData.pickup_time}:00`;
      const bookingPayload = {
        passenger_id: newPassengerId,
        start: formData.pickup_location,
        end: formData.dropoff_location,
        journey_date: journeyDateTime,
        fleet_id: parseInt(formData.fleet_id),
        payment_method: formData.payment_type === 'FULL' ? 'Square Payment' : 'Square Payment ($1)',
        domain: window.location.hostname,
        passenger_count: parseInt(formData.passenger_count),
        special_requirements: formData.special_instructions,
        flight_number: formData.flight_number,
        fare_details: {
          selected_fleet_id: parseInt(formData.fleet_id),
          journey_fare: parseFloat(formData.journey_fare).toFixed(2),
          add_charges: [],
          other_add_charges: [],
          discount: "0.00",
          final_fare: parseFloat(formData.final_fare).toFixed(2)
        }
      };

      const bookingResponse = await createBooking(bookingPayload);
      const newBookingRefId = bookingResponse.booking_ref_id || bookingResponse.id;
      
      setBookingRefId(newBookingRefId);
      setBookingData({
        ...formData,
        passenger_id: newPassengerId,
        booking_ref_id: newBookingRefId
      });
      
      setStep('payment');
    } catch (error) {
      console.error('Booking creation failed:', error);
      setErrors({ 
        submit: error.message || 'Failed to create booking. Please try again.' 
      });
      if (onError) {
        onError(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaymentSuccess = (paymentResult) => {
    console.log('Payment successful:', paymentResult);
    setStep('complete');
    
    if (onBookingComplete) {
      onBookingComplete({
        booking: bookingData,
        payment: paymentResult,
        bookingRefId,
        passengerId
      });
    }
  };

  const handlePaymentError = (error) => {
    console.error('Payment failed:', error);
    setErrors({ payment: error });
    if (onError) {
      onError(error);
    }
  };

  const renderBookingForm = () => (
    <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Booking Details</h2>
      
      <form onSubmit={handleBookingSubmit} className="space-y-6">
        {/* Passenger Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              First Name *
            </label>
            <input
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
              className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                errors.first_name ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter first name"
            />
            {errors.first_name && (
              <p className="text-red-500 text-sm mt-1">{errors.first_name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Last Name *
            </label>
            <input
              type="text"
              name="last_name"
              value={formData.last_name}
              onChange={handleInputChange}
              className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                errors.last_name ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter last name"
            />
            {errors.last_name && (
              <p className="text-red-500 text-sm mt-1">{errors.last_name}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter email address"
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone *
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                errors.phone ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter phone number"
            />
            {errors.phone && (
              <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
            )}
          </div>
        </div>

        {/* Trip Information */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Trip Information</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pickup Location *
              </label>
              <input
                type="text"
                name="pickup_location"
                value={formData.pickup_location}
                onChange={handleInputChange}
                className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                  errors.pickup_location ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter pickup address"
              />
              {errors.pickup_location && (
                <p className="text-red-500 text-sm mt-1">{errors.pickup_location}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dropoff Location *
              </label>
              <input
                type="text"
                name="dropoff_location"
                value={formData.dropoff_location}
                onChange={handleInputChange}
                className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                  errors.dropoff_location ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter dropoff address"
              />
              {errors.dropoff_location && (
                <p className="text-red-500 text-sm mt-1">{errors.dropoff_location}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pickup Date *
                </label>
                <input
                  type="date"
                  name="pickup_date"
                  value={formData.pickup_date}
                  onChange={handleInputChange}
                  className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                    errors.pickup_date ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.pickup_date && (
                  <p className="text-red-500 text-sm mt-1">{errors.pickup_date}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pickup Time *
                </label>
                <input
                  type="time"
                  name="pickup_time"
                  value={formData.pickup_time}
                  onChange={handleInputChange}
                  className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                    errors.pickup_time ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.pickup_time && (
                  <p className="text-red-500 text-sm mt-1">{errors.pickup_time}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Final Fare *
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="final_fare"
                  value={formData.final_fare}
                  onChange={handleInputChange}
                  className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                    errors.final_fare ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="0.00"
                />
                {errors.final_fare && (
                  <p className="text-red-500 text-sm mt-1">{errors.final_fare}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Type
                </label>
                <select
                  name="payment_type"
                  value={formData.payment_type}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="FULL">Full Payment</option>
                  <option value="PARTIAL">Partial Payment ($1.00)</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Flight Number (Optional)
              </label>
              <input
                type="text"
                name="flight_number"
                value={formData.flight_number}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="Enter flight number"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Special Instructions (Optional)
              </label>
              <textarea
                name="special_instructions"
                value={formData.special_instructions}
                onChange={handleInputChange}
                rows="3"
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="Any special requests or instructions"
              />
            </div>
          </div>
        </div>

        {errors.submit && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-700 text-sm">{errors.submit}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className={`w-full py-3 px-4 rounded-md font-medium text-white transition-colors ${
            isLoading
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500'
          }`}
        >
          {isLoading ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              Creating Booking...
            </div>
          ) : (
            'Continue to Payment'
          )}
        </button>
      </form>
    </div>
  );

  const renderPaymentStep = () => (
    <div className="space-y-6">
      <div className="max-w-md mx-auto text-center">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Complete Payment</h2>
        <p className="text-gray-600">Booking Reference: {bookingRefId}</p>
      </div>
      
      <SquarePayment
        bookingRefId={bookingRefId}
        amount={parseFloat(formData.final_fare)}
        passengerId={passengerId}
        paymentType={formData.payment_type}
        onSuccess={handlePaymentSuccess}
        onError={handlePaymentError}
      />
      
      <div className="max-w-md mx-auto text-center">
        <button
          onClick={() => setStep('booking')}
          className="text-blue-600 hover:text-blue-800 text-sm"
        >
          ← Back to booking details
        </button>
      </div>
    </div>
  );

  const renderCompleteStep = () => (
    <div className="max-w-md mx-auto text-center p-6 bg-white rounded-lg shadow-lg">
      <div className="mb-6">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Booking Confirmed!</h2>
        <p className="text-gray-600">Your payment has been processed successfully.</p>
      </div>
      
      <div className="bg-gray-50 p-4 rounded-md mb-6">
        <p className="text-sm text-gray-600 mb-1">Booking Reference</p>
        <p className="text-lg font-semibold text-gray-800">{bookingRefId}</p>
      </div>
      
      <div className="text-sm text-gray-600 space-y-1">
        <p>A confirmation email has been sent to {formData.email}</p>
        <p>You will receive updates about your booking via SMS and email.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        {/* Progress indicator */}
        <div className="max-w-md mx-auto mb-8">
          <div className="flex items-center justify-between">
            <div className={`flex items-center ${step === 'booking' ? 'text-blue-600' : 'text-green-600'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step === 'booking' ? 'bg-blue-600 text-white' : 'bg-green-600 text-white'
              }`}>
                {step === 'booking' ? '1' : '✓'}
              </div>
              <span className="ml-2 text-sm font-medium">Booking</span>
            </div>
            
            <div className={`flex-1 h-1 mx-4 ${
              ['payment', 'complete'].includes(step) ? 'bg-green-600' : 'bg-gray-300'
            }`}></div>
            
            <div className={`flex items-center ${
              step === 'payment' ? 'text-blue-600' : 
              step === 'complete' ? 'text-green-600' : 'text-gray-400'
            }`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step === 'payment' ? 'bg-blue-600 text-white' :
                step === 'complete' ? 'bg-green-600 text-white' : 'bg-gray-300 text-gray-600'
              }`}>
                {step === 'complete' ? '✓' : '2'}
              </div>
              <span className="ml-2 text-sm font-medium">Payment</span>
            </div>
            
            <div className={`flex-1 h-1 mx-4 ${
              step === 'complete' ? 'bg-green-600' : 'bg-gray-300'
            }`}></div>
            
            <div className={`flex items-center ${
              step === 'complete' ? 'text-green-600' : 'text-gray-400'
            }`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step === 'complete' ? 'bg-green-600 text-white' : 'bg-gray-300 text-gray-600'
              }`}>
                {step === 'complete' ? '✓' : '3'}
              </div>
              <span className="ml-2 text-sm font-medium">Complete</span>
            </div>
          </div>
        </div>

        {/* Step content */}
        {step === 'booking' && renderBookingForm()}
        {step === 'payment' && renderPaymentStep()}
        {step === 'complete' && renderCompleteStep()}
      </div>
    </div>
  );
};

export default BookingFlow;
